import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { agencyService } from '../../services/api';
import type { Agency } from '../../types';

const Agencies: React.FC = () => {
  const [list, setList] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Agency | null>(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    address: '',
    address_line_2: '',
    city: '',
    postal_code: '',
    country: 'France',
    phone: '',
    email: '',
    is_active: true,
    sort_order: 0,
  });
  const [saveLoading, setSaveLoading] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await agencyService.getAll();
      setList(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur chargement agences.');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleOpen = (agency?: Agency) => {
    if (agency) {
      setEditing(agency);
      setForm({
        name: agency.name,
        code: agency.code || '',
        address: agency.address || '',
        address_line_2: agency.address_line_2 || '',
        city: agency.city || '',
        postal_code: agency.postal_code || '',
        country: agency.country || 'France',
        phone: agency.phone || '',
        email: agency.email || '',
        is_active: agency.is_active ?? true,
        sort_order: agency.sort_order ?? 0,
      });
    } else {
      setEditing(null);
      setForm({
        name: '',
        code: '',
        address: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        country: 'France',
        phone: '',
        email: '',
        is_active: true,
        sort_order: 0,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Le nom est obligatoire.');
      return;
    }
    setSaveLoading(true);
    setError(null);
    try {
      if (editing) {
        await agencyService.update(editing.id, form);
      } else {
        await agencyService.create(form);
      }
      handleClose();
      fetchList();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur enregistrement.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette agence ?')) return;
    try {
      await agencyService.delete(id);
      fetchList();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur suppression.');
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        AGENCES
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Lieux de départ et de retour pour les réservations.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          + Ajouter une agence
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main', '& th': { color: 'white', fontWeight: 600 } }}>
                <TableCell>Code</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Ville</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="center">Actif</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    Aucune agence. Ajoutez-en une pour les réservations.
                  </TableCell>
                </TableRow>
              ) : (
                list.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.code || '—'}</TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>{a.city || '—'}</TableCell>
                    <TableCell>{a.phone || '—'}</TableCell>
                    <TableCell>{a.email || '—'}</TableCell>
                    <TableCell align="center">{a.is_active ? 'Oui' : 'Non'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpen(a)} title="Modifier">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(a.id)} title="Supprimer">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Modifier l\'agence' : 'Nouvelle agence'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField fullWidth size="small" label="Nom" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField fullWidth size="small" label="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            <TextField fullWidth size="small" label="Adresse" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            <TextField fullWidth size="small" label="Complément" value={form.address_line_2} onChange={(e) => setForm((f) => ({ ...f, address_line_2: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth size="small" label="Code postal" value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} />
              <TextField fullWidth size="small" label="Ville" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
            </Box>
            <TextField fullWidth size="small" label="Pays" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} />
            <TextField fullWidth size="small" label="Téléphone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            <TextField fullWidth size="small" label="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            <TextField fullWidth size="small" label="Ordre" type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveLoading}>{saveLoading ? <CircularProgress size={24} /> : 'Enregistrer'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agencies;
